import { Capacitor, registerPlugin } from "@capacitor/core"
import type { PluginListenerHandle } from "@capacitor/core"

export interface NativePushToken {
  kind: "standard" | "voip"
  token: string
  environment: "development" | "production"
  deviceName?: string
}

export interface NativeCallAction {
  action: "incoming" | "accept" | "end"
  callId: string
  name?: string
  hasVideo?: boolean
}

interface NotificationRegistration extends Partial<NativePushToken> {
  granted: boolean
  standardToken?: string
  voipToken?: string
  error?: string
}

interface JoowFakNativePlugin {
  registerNotifications(): Promise<NotificationRegistration>
  pendingActions(): Promise<{ items: NativeCallAction[] }>
  preparePictureInPicture(options: { url: string; token: string; selfIdentity?: string; peerName?: string }): Promise<{ supported: boolean }>
  startPictureInPicture(): Promise<{ started: boolean }>
  stopPictureInPicture(): Promise<void>
  endPictureInPicture(): Promise<void>
  startOutgoingCall(options: { callId: string; name: string; hasVideo: boolean }): Promise<void>
  answerSystemCall(options: { callId: string }): Promise<void>
  reportCallConnected(options: { callId: string }): Promise<void>
  endSystemCall(options: { callId: string }): Promise<void>
  addListener(eventName: "pushToken", listener: (event: NativePushToken) => void): Promise<PluginListenerHandle>
  addListener(eventName: "nativeCallAction", listener: (event: NativeCallAction) => void): Promise<PluginListenerHandle>
  addListener(eventName: "notificationTapped", listener: (event: { resourceId?: string; kind?: string }) => void): Promise<PluginListenerHandle>
  addListener(eventName: "pictureInPictureRestore", listener: () => void): Promise<PluginListenerHandle>
}

const nativePlugin = registerPlugin<JoowFakNativePlugin>("JoowFakNative")

export function isNativeIOS(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios"
}

export async function registerNativeNotifications(): Promise<NotificationRegistration | null> {
  if (!isNativeIOS()) return null
  return nativePlugin.registerNotifications()
}

export async function pendingNativeCallActions(): Promise<NativeCallAction[]> {
  if (!isNativeIOS()) return []
  return (await nativePlugin.pendingActions()).items || []
}

export async function onNativePushToken(listener: (event: NativePushToken) => void): Promise<PluginListenerHandle | null> {
  if (!isNativeIOS()) return null
  return nativePlugin.addListener("pushToken", listener)
}

export async function onNativeCallAction(listener: (event: NativeCallAction) => void): Promise<PluginListenerHandle | null> {
  if (!isNativeIOS()) return null
  return nativePlugin.addListener("nativeCallAction", listener)
}

export async function onNativeNotificationTap(listener: (event: { resourceId?: string; kind?: string }) => void): Promise<PluginListenerHandle | null> {
  if (!isNativeIOS()) return null
  return nativePlugin.addListener("notificationTapped", listener)
}

export async function onNativePiPRestore(listener: () => void): Promise<PluginListenerHandle | null> {
  if (!isNativeIOS()) return null
  return nativePlugin.addListener("pictureInPictureRestore", listener)
}

export async function prepareNativePictureInPicture(signaling: { url: string; pipTicket?: string; selfIdentity?: string }, peerName: string): Promise<boolean> {
  if (!isNativeIOS() || !signaling.pipTicket) return false
  const result = await nativePlugin.preparePictureInPicture({
    url: signaling.url,
    token: signaling.pipTicket,
    selfIdentity: signaling.selfIdentity,
    peerName,
  })
  return result.supported
}

export async function startNativePictureInPicture(): Promise<boolean> {
  if (!isNativeIOS()) return false
  try {
    return (await nativePlugin.startPictureInPicture()).started
  } catch {
    return false
  }
}

export async function stopNativePictureInPicture(): Promise<void> {
  if (isNativeIOS()) await nativePlugin.stopPictureInPicture()
}

export async function endNativePictureInPicture(): Promise<void> {
  if (isNativeIOS()) await nativePlugin.endPictureInPicture()
}

export async function startNativeOutgoingCall(callId: string, name: string, hasVideo: boolean): Promise<void> {
  if (isNativeIOS()) await nativePlugin.startOutgoingCall({ callId, name, hasVideo })
}

export async function reportNativeCallConnected(callId: string): Promise<void> {
  if (isNativeIOS()) await nativePlugin.reportCallConnected({ callId })
}

export async function answerNativeSystemCall(callId: string): Promise<void> {
  if (isNativeIOS()) await nativePlugin.answerSystemCall({ callId })
}

export async function endNativeSystemCall(callId: string): Promise<void> {
  if (isNativeIOS()) await nativePlugin.endSystemCall({ callId })
}
